import apiClient from "./apiClient";
import AppError from "../utils/AppError";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../utils/notifications";

const UserService = {
  async getCurrentUserOrganisations() {
    try {
      const response = await apiClient.get("/users/me/organisations");
      // Backend ships `country`; map it to `countryCode`, the key consumers read and the other org endpoints already emit.
      return (response.data || []).map((o) => ({
        ...o,
        countryCode: o.countryCode ?? o.country ?? null,
      }));
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Load Organisations",
        response: error.response,
        userMessage: "Could not load organisation options. Please try again.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async getUsers({
    page = 1,
    page_size = 25,
    search,
    ordering,
    organisation,
    is_active,
    expiring,
  } = {}) {
    try {
      const response = await apiClient.get("/users", {
        params: {
          page,
          page_size,
          search,
          ordering,
          organisation,
          is_active,
          expiring,
        },
      });
      return response.data || [];
    } catch (error) {
      // A 404 from this list endpoint means the requested page is out of range
      // (e.g. a persisted page that no longer exists for the current org or
      // filter). Surface the raw error so the caller can snap back to a valid
      // page instead of showing a "Failed to Load Users" toast.
      if (error.response?.status === 404) {
        throw error;
      }
      console.error("Error fetching users:", error);
      const appError = new AppError({
        title: "Failed to Load Users",
        response: error.response,
        userMessage: "Could not load users. Please try again.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async getUserCounts({ search, organisation } = {}) {
    try {
      const response = await apiClient.get("/users/counts", {
        params: { search, organisation },
      });
      return response.data || { all: 0, active: 0, inactive: 0, expiring: 0 };
    } catch (error) {
      console.error("Error fetching user counts:", error);
      // Counts are an enhancement, not critical — degrade silently rather
      // than spamming notifications when the backend hasn't shipped yet.
      return null;
    }
  },

  async createUser(payload) {
    try {
      const { data } = await apiClient.post("/users", payload);
      showSuccessNotification(
        "User Created",
        `User "${payload.firstName} ${payload.lastName}" has been successfully created.`
      );
      return data;
    } catch (error) {
      let userMessage = "Could not create the user. Please try again.";
      if (error.response && error.response.status === 400) {
        const errorData = error.response.data;
        if (typeof errorData === "object" && errorData !== null) {
          userMessage = "";
          Object.entries(errorData).forEach(([key, value]) => {
            userMessage += `${key}: ${value}\n`;
          });
        } else {
          userMessage = errorData;
        }
      }
      console.error("Error creating user:", error);
      const appError = new AppError({
        title: "Failed to Create User",
        response: error.response,
        userMessage,
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async getUser(id) {
    try {
      const { data } = await apiClient.get(`/users/${id}`);
      return data;
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Fetch User",
        response: error.response,
        userMessage: "Could not fetch the user. Please try again.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async patchUser(id, payload) {
    try {
      const { data } = await apiClient.patch(`/users/${id}`, payload);
      showSuccessNotification(
        "User Updated",
        `User "${data.firstName} ${data.lastName}" has been successfully updated.`
      );
      return data;
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Update User",
        response: error.response,
        userMessage: "Could not update the user. Please try again.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async sendActivationEmail(id) {
    try {
      const { data } = await apiClient.post(`/users/${id}/resend-activation`);
      showSuccessNotification(
        "Activation Email Sent",
        "The activation email has been successfully sent."
      );
      return data;
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Send Activation Email",
        response: error.response,
        userMessage: "Could not resend the activation email. Please try again.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async sendPasswordResetEmail(id) {
    try {
      const { data } = await apiClient.post(`/users/${id}/reset-password`);
      showSuccessNotification(
        "Password Reset Email Sent",
        "The password reset email has been successfully sent."
      );
      return data;
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Send Password Reset Email",
        response: error.response,
        userMessage:
          "Could not send the password reset email. Please try again.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },
};

export default UserService;
